/**
 * Carrier packaging offered when buying a label (Buy label → Packaging). Most entries are Shippo parcel templates
 * (`template` = the token of Shippo's `ParcelTemplateEnumSet`, https://docs.goshippo.com/spec/shippoapi/public-api.yaml):
 * Shippo uses the template's own dimensions, so only the weight is sent, and a carrier template only returns that
 * carrier's services. Dimensions below are Shippo's (for display). Shippo has no DHL Express templates: DHL Express
 * packaging is sent as plain dimensions (`template: null`), so other carriers may still quote it.
 *
 * Left out on purpose: USPS Regional Rate boxes (discontinued by USPS), USPS Soft Pack and Irregular Parcel and the
 * UPS Mail Innovations tokens (no fixed size), the UPS pallet and laboratory pak, and DHL eCommerce (a different
 * service from DHL Express).
 */

export type PackageCarrier = 'ups' | 'usps' | 'fedex' | 'dhl';

export interface CarrierPackage {
  /** Stable key (the Shippo token for templates). */
  key: string;
  carrier: PackageCarrier;
  /** Shippo parcel template token, or null when the package is sent as dimensions. */
  template: string | null;
  label: string;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  /** The carrier's weight limit for the package when known (a warning only). */
  maxWeightKg: number | null;
}

export interface CarrierPackageGroup {
  carrier: PackageCarrier;
  /** Carrier name as Shippo reports it on rates (`provider`), used for the carrier filter. */
  name: string;
  packages: CarrierPackage[];
}

const IN = 2.54;
const LB = 0.45359237;
const OZ = LB / 16;
const r2 = (n: number) => Math.round(n * 100) / 100;

type Row = [key: string, label: string, dims: [number, number, number], unit: 'in' | 'mm' | 'cm', maxWeightKg?: number | null, template?: boolean];

function group(carrier: PackageCarrier, name: string, rows: Row[]): CarrierPackageGroup {
  return {
    carrier,
    name,
    packages: rows.map(([key, label, [l, w, h], unit, maxWeightKg = null, template = true]) => {
      const cm = (v: number) => (unit === 'in' ? v * IN : unit === 'mm' ? v / 10 : v);
      return {
        key,
        carrier,
        template: template ? key : null,
        label,
        lengthIn: r2(cm(l) / IN),
        widthIn: r2(cm(w) / IN),
        heightIn: r2(cm(h) / IN),
        lengthCm: r2(cm(l)),
        widthCm: r2(cm(w)),
        heightCm: r2(cm(h)),
        maxWeightKg: maxWeightKg == null ? null : +maxWeightKg.toFixed(3),
      };
    }),
  };
}

const USPS_FLAT_RATE_MAX = 70 * LB;

export const CARRIER_PACKAGES: readonly CarrierPackageGroup[] = [
  group('ups', 'UPS', [
    ['UPS_Express_Envelope', 'UPS Express Envelope', [12.5, 9.5, 2], 'in', 8 * OZ],
    ['UPS_Express_Legal_Envelope', 'UPS Express Legal Envelope', [15, 9.5, 2], 'in', 8 * OZ],
    ['UPS_Express_Pak', 'UPS Express Pak', [16, 12.75, 2], 'in'],
    ['UPS_Pad_Pak', 'UPS Pad Pak', [14.75, 11, 2], 'in'],
    ['UPS_Express_Hard_Pak', 'UPS Express Hard Pak', [14.75, 11.5, 2], 'in'],
    ['UPS_Express_Box_Small', 'UPS Express Box (small)', [13, 11, 2], 'in'],
    ['UPS_Express_Box_Medium', 'UPS Express Box (medium)', [15, 11, 3], 'in'],
    ['UPS_Express_Box_Large', 'UPS Express Box (large)', [18, 13, 3], 'in'],
    ['UPS_Express_Box', 'UPS Express Box', [460, 315, 95], 'mm'],
    ['UPS_Express_Tube', 'UPS Express Tube', [970, 190, 165], 'mm'],
    ['UPS_Box_10kg', 'UPS 10 kg Box', [410, 335, 265], 'mm', 10],
    ['UPS_Box_25kg', 'UPS 25 kg Box', [484, 433, 350], 'mm', 25],
  ]),
  group('usps', 'USPS', [
    ['USPS_FlatRateEnvelope', 'USPS Flat Rate Envelope', [12.5, 9.5, 0.75], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_FlatRateLegalEnvelope', 'USPS Flat Rate Legal Envelope', [15, 9.5, 0.75], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_FlatRatePaddedEnvelope', 'USPS Flat Rate Padded Envelope', [12.5, 9.5, 1], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_FlatRateCardboardEnvelope', 'USPS Flat Rate Cardboard Envelope', [12.5, 9.5, 0.75], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_FlatRateWindowEnvelope', 'USPS Flat Rate Window Envelope', [10, 5, 0.75], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_FlatRateGiftCardEnvelope', 'USPS Flat Rate Gift Card Envelope', [10, 7, 0.75], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_SmallFlatRateEnvelope', 'USPS Small Flat Rate Envelope', [10, 6, 4], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_SmallFlatRateBox', 'USPS Small Flat Rate Box', [8.69, 5.44, 1.75], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_MediumFlatRateBox1', 'USPS Medium Flat Rate Box (top-loading)', [11.25, 8.75, 6], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_MediumFlatRateBox2', 'USPS Medium Flat Rate Box (side-loading)', [14, 12, 3.5], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_LargeFlatRateBox', 'USPS Large Flat Rate Box', [12.25, 12.25, 6], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_LargeFlatRateBoardGameBox', 'USPS Large Flat Rate Board Game Box', [24.06, 11.88, 3.13], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_APOFlatRateBox', 'USPS APO/FPO/DPO Large Flat Rate Box', [12.25, 12.25, 6], 'in', USPS_FLAT_RATE_MAX],
    ['USPS_LargeVideoFlatRateBox', 'USPS Flat Rate Large Video Box (international only)', [9.6, 6.4, 2.2], 'in', USPS_FLAT_RATE_MAX],
  ]),
  group('fedex', 'FedEx', [
    ['FedEx_Envelope', 'FedEx Envelope', [12.5, 9.5, 0.8], 'in', 8 * OZ],
    ['FedEx_Pak_2', 'FedEx Pak (small)', [12.75, 10.25, 0.8], 'in'],
    ['FedEx_Pak_1', 'FedEx Pak (large)', [15.5, 12, 0.8], 'in'],
    ['FedEx_Padded_Pak', 'FedEx Padded Pak', [11.75, 14.75, 2], 'in'],
    ['FedEx_XL_Pak', 'FedEx Extra Large Pak', [17.5, 20.75, 2], 'in'],
    ['FedEx_Box_Small_1', 'FedEx Small Box (S1)', [12.38, 10.88, 1.5], 'in'],
    ['FedEx_Box_Small_2', 'FedEx Small Box (S2)', [11.25, 8.75, 2.38], 'in'],
    ['FedEx_Box_Medium_1', 'FedEx Medium Box (M1)', [13.25, 11.5, 2.38], 'in'],
    ['FedEx_Box_Medium_2', 'FedEx Medium Box (M2)', [11.25, 8.75, 4.38], 'in'],
    ['FedEx_Box_Large_1', 'FedEx Large Box (L1)', [17.5, 12.38, 3], 'in'],
    ['FedEx_Box_Large_2', 'FedEx Large Box (L2)', [11.25, 8.75, 7.75], 'in'],
    ['FedEx_Box_Extra_Large_1', 'FedEx Extra Large Box (X1)', [11.88, 11, 10.75], 'in'],
    ['FedEx_Box_Extra_Large_2', 'FedEx Extra Large Box (X2)', [15.75, 14.13, 6], 'in'],
    ['FedEx_Tube', 'FedEx Tube', [38, 6, 6], 'in'],
    ['FedEx_Box_10kg', 'FedEx 10 kg Box', [15.81, 12.94, 10.19], 'in', 10],
    ['FedEx_Box_25kg', 'FedEx 25 kg Box', [54.8, 42.1, 33.5], 'in', 25],
  ]),
  // No Shippo templates for DHL Express: sent as dimensions (DHL Express packaging guide sizes).
  group('dhl', 'DHL Express', [
    ['dhl_express_envelope', 'DHL Express Envelope', [35, 27.5, 1], 'cm', 0.3, false],
    ['dhl_express_flyer', 'DHL Express Flyer (standard)', [40, 30, 2], 'cm', null, false],
    ['dhl_express_flyer_large', 'DHL Express Flyer (large)', [47.5, 37.5, 2], 'cm', null, false],
  ]),
];

export const CARRIER_PACKAGE_LIST: readonly CarrierPackage[] = CARRIER_PACKAGES.flatMap((g) => g.packages);

/** Shippo parcel template tokens accepted by the label rates / purchase endpoints. */
export const PARCEL_TEMPLATES: readonly string[] = CARRIER_PACKAGE_LIST.flatMap((p) => (p.template ? [p.template] : []));

export const isParcelTemplate = (token: string | null | undefined): boolean => !!token && PARCEL_TEMPLATES.includes(token);

export const findCarrierPackage = (key: string | null | undefined): CarrierPackage | undefined => (key ? CARRIER_PACKAGE_LIST.find((p) => p.key === key || p.template === key) : undefined);

/** Shippo carrier name ("UPS", "FedEx", "DHL Express") of a package. */
export const packageCarrierName = (p: Pick<CarrierPackage, 'carrier'>): string => CARRIER_PACKAGES.find((g) => g.carrier === p.carrier)!.name;
